// src/cashup/LaborTab.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select from 'react-select';
import Draggable from 'react-draggable';
import { CSVLink } from 'react-csv';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import Modal from '../components/Modal';
import Tooltip from '../components/Tooltip';
import {
  addDoc,
  collection,
  query as q,
  where,
  getDoc,
  getDocs,
  orderBy,
  limit,
  onSnapshot,
  doc,
  updateDoc,
  setDoc,
  deleteDoc,
    Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';
import { useOrgId, useOrgMeta } from '../utils/tenant';
import useActor from '../utils/useActor';
import { logAudit } from '../utils/audit';
import { toast } from 'react-toastify';

// — react-select dark theme overrides —
const selectTheme = (theme) => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary25: '#334155',
    primary: '#1e293b',
    neutral0: '#1e293b',
    neutral80: '#ffffff',
  },
});
const selectStyles = {
  control: (base) => ({ ...base, backgroundColor: '#1e293b', borderColor: '#334155', color: '#fff' }),
  menu: (base) => ({ ...base, backgroundColor: '#1e293b' }),
  option: (base) => ({ ...base, backgroundColor: '#1e293b', color: '#fff' }),
};

// --- helpers for timestamps / row shape ---
const toSec = (v) =>
  v?.seconds ??
  (v?.toDate ? Math.floor(v.toDate().getTime() / 1000) : typeof v === 'number' ? v : null);

const asTS = (secOrNull) =>
  typeof secOrNull === 'number'
    ? Timestamp.fromMillis(secOrNull * 1000)
    : null;


const approvedShiftSeconds = (shiftText, rawClockInSec) => {
  const match = String(shiftText || '').match(/^(\d{2}):(\d{2})-(\d{2}):(\d{2})$/);
  if (!match || typeof rawClockInSec !== 'number') return { start: null, end: null };

  const base = new Date(rawClockInSec * 1000);
  const start = new Date(base);
  start.setHours(Number(match[1]), Number(match[2]), 0, 0);

  const end = new Date(base);
  end.setHours(Number(match[3]), Number(match[4]), 0, 0);
  if (end <= start) end.setDate(end.getDate() + 1);

  return {
    start: Math.floor(start.getTime() / 1000),
    end: Math.floor(end.getTime() / 1000),
  };
};

const normalizeShiftDoc = (snap, pathArray, storeIdFromPath) => {
  const d = snap.data() || {};
  const rawCi = toSec(d.clockIn) ?? toSec(d.clockInAt) ?? toSec(d.in) ?? toSec(d.inAt);
  const rawCo = toSec(d.clockOut) ?? toSec(d.clockOutAt) ?? toSec(d.out) ?? toSec(d.outAt);
  const approved = d.scheduleVarianceApproved === true;
  const approvedFromShift = approvedShiftSeconds(d.approvedScheduleShift, rawCi);
  const approvedCi =
    toSec(d.approvedActualClockInSec) ??
    (approved ? approvedFromShift.start : null);
  const approvedCo =
    toSec(d.approvedActualClockOutSec) ??
    (approved ? approvedFromShift.end : null);

  // Phase 3: actual clocks remain immutable evidence.
  // Approval changes payable classification, not what the person actually clocked.
  const ci = rawCi;
  const co = rawCo;
  const oci = toSec(d.oldClockIn);
  const oco = toSec(d.oldClockOut);
  const actualCo = toSec(d.actualClockOut);
  const thresholdCo = toSec(d.thresholdClockOut);

  return {
    id: snap.id,
    name: d.name || d.staffName || d.employeeName || '',
    role: d.role || d.roleName || '',
    store: d.store || d.storeId || storeIdFromPath || '',
    clockIn: ci != null ? { seconds: ci } : null,
    clockOut: co != null ? { seconds: co } : null,
    oldClockIn: oci != null ? { seconds: oci } : undefined,
    oldClockOut: oco != null ? { seconds: oco } : undefined,
    actualClockOut: actualCo != null ? { seconds: actualCo } : undefined,
    thresholdClockOut: thresholdCo != null ? { seconds: thresholdCo } : undefined,
    autoClockedOutAtThreshold: d.autoClockedOutAtThreshold === true,
    autoClockOutMode: d.autoClockOutMode || '',
    // Optional per-shift lunch override (minutes)
    lunchMin:
      typeof d.lunchMin === 'number'
        ? d.lunchMin
        : typeof d.lunchMinutes === 'number'
          ? d.lunchMinutes
          : null,
    reason: d.reason || '',
    archived: !!(d.archived || d.isArchived),
    archivedAt: d.archivedAt || null,
    archivedBy: d.archivedBy || d.archivedByEmail || d.archivedByUid || '',
    staffId: d.staffId || d.userId || d.userUid || d.uid || d.employeeId || '',
    staffNameSnapshot: d.staffName || d.staffNameSnapshot || d.name || '',
    // Audit / edit meta (optional)
    editedBy: d.editedBy || d.updatedBy || d.modifiedBy || '',
    editedAt: d.editedAt || d.updatedAt || null,
    scheduleVarianceApproved: d.scheduleVarianceApproved === true,
    scheduleVarianceRejected: d.scheduleVarianceRejected === true,
    payrollZeroHours: d.payrollZeroHours === true,
    scheduleVarianceApprovedAt: d.scheduleVarianceApprovedAt || null,
    scheduleVarianceApprovedByName: d.scheduleVarianceApprovedByName || d.scheduleVarianceApprovedByEmail || '',
    approvedScheduleShift: d.approvedScheduleShift || '',
    originalScheduledShift: d.originalScheduledShift || '',
    scheduleVarianceType: d.scheduleVarianceType || '',
    swapExpectedStaffId: d.swapExpectedStaffId || '',
    swapExpectedStaffName: d.swapExpectedStaffName || '',
    swapReplacementStaffId: d.swapReplacementStaffId || '',
    swapReplacementStaffName: d.swapReplacementStaffName || '',
    approvedActualClockInSec: approvedCi,
    approvedActualClockOutSec: approvedCo,
    __path: pathArray, // where to write back
  };
};

// end-of-day helper
const endOfDayMsForClockIn = (ciSeconds) => {
  const d = new Date(ciSeconds * 1000);
  d.setHours(23, 59, 59, 999);
  return d.getTime();
};

const formatTimeSec = (sec) =>
  typeof sec === 'number'
    ? new Date(sec * 1000).toLocaleTimeString('en-GB', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
      })
    : '--:--';

const isLegacyEndOfDayAutoOut = (shift, abandonedHours = 18) => {
  const ciSec = toSec(shift?.clockIn);
  const coSec = toSec(shift?.clockOut);
  const actualSec = toSec(shift?.actualClockOut);
  const thresholdSec = toSec(shift?.thresholdClockOut);

  if (typeof ciSec !== 'number' || typeof coSec !== 'number') return false;
  if (typeof actualSec === 'number' || typeof thresholdSec === 'number' || shift?.autoClockedOutAtThreshold) return false;

  const ci = new Date(ciSec * 1000);
  const co = new Date(coSec * 1000);

  const sameCalendarDay =
    ci.getFullYear() === co.getFullYear() &&
    ci.getMonth() === co.getMonth() &&
    ci.getDate() === co.getDate();

  if (!(sameCalendarDay && co.getHours() === 23 && co.getMinutes() === 59)) return false;

  const rawHours = (coSec - ciSec) / 3600;
  return rawHours > Math.max(1, Number(abandonedHours) || 18);
};

const getThresholdCutoffSec = (shift, abandonedHours = 18) => {
  const ciSec = toSec(shift?.clockIn);
  if (typeof ciSec !== 'number') return null;
  return ciSec + Math.max(1, Number(abandonedHours) || 18) * 3600;
};

const getDisplayClockOutSec = (shift, abandonedHours = 18) => {
  // Always show the real recorded/final clock-out first.
  const coSec = toSec(shift?.clockOut);
  if (typeof coSec === 'number') return coSec;

  const actualSec = toSec(shift?.actualClockOut);
  if (typeof actualSec === 'number') return actualSec;

  // Backward compatibility for older approved records only.
  const approvedOutSec = toSec(shift?.approvedActualClockOutSec);
  if (typeof approvedOutSec === 'number') return approvedOutSec;

  return null;
};



const getEditClockOutSec = (shift, abandonedHours = 18) => {
  return getDisplayClockOutSec(shift, abandonedHours);
};

const buildEditedClockOutSec = (shift, nextHour = null, nextMinute = null, abandonedHours = 18) => {
  const clockInSec = toSec(shift?.clockIn);
  if (typeof clockInSec !== 'number') return null;

  const clockInDate = new Date(clockInSec * 1000);
  const currentEditSec = getEditClockOutSec(shift, abandonedHours);
  const currentEditDate =
    typeof currentEditSec === 'number' ? new Date(currentEditSec * 1000) : null;

  // Always anchor manual edits to the clock-in calendar day first.
  // Then only roll into the next day when the chosen clock-out time is
  // earlier than the clock-in time. This prevents a shift that is currently
  // showing next-day auto clock-out (for example 00:14) from jumping to the
  // following night (for example 22:00 next day) when edited.
  const d = new Date(clockInDate.getTime());

  if (currentEditDate) {
    d.setHours(currentEditDate.getHours(), currentEditDate.getMinutes(), 0, 0);
  } else {
    d.setHours(clockInDate.getHours(), clockInDate.getMinutes(), 0, 0);
  }

  if (typeof nextHour === 'number') d.setHours(nextHour);
  if (typeof nextMinute === 'number') d.setMinutes(nextMinute);
  d.setSeconds(0, 0);

  const ciMinutes = clockInDate.getHours() * 60 + clockInDate.getMinutes();
  const outMinutes = d.getHours() * 60 + d.getMinutes();

  if (outMinutes < ciMinutes) {
    d.setDate(d.getDate() + 1);
  }

  return Math.floor(d.getTime() / 1000);
};

const getOriginalClockOutSec = (shift, abandonedHours = 18) => {
  const displaySec = getDisplayClockOutSec(shift, abandonedHours);
  const actualSec = toSec(shift?.actualClockOut);
  const oldSec = toSec(shift?.oldClockOut);
  const currentSec = toSec(shift?.clockOut);
  const thresholdSec = toSec(shift?.thresholdClockOut);

  if (typeof actualSec === 'number' && actualSec !== displaySec) return actualSec;
  if (typeof oldSec === 'number' && oldSec !== displaySec) return oldSec;
  if (typeof currentSec === 'number' && currentSec !== displaySec) return currentSec;
  if (typeof thresholdSec === 'number' && thresholdSec !== displaySec) return thresholdSec;
  return null;
};

const canRestoreShift = (shift, abandonedHours = 18) => {
  if (shift?.scheduleVarianceApproved === true) return false;

  const originalSec = getOriginalClockOutSec(shift, abandonedHours);
  const editSec = getEditClockOutSec(shift, abandonedHours);
  return (
    typeof originalSec === 'number' &&
    typeof editSec === 'number' &&
    Math.floor(originalSec / 60) !== Math.floor(editSec / 60)
  );
};

const buildClockOutDate = (ciDate, shift, abandonedHours = 18) => {
  const outSec = getDisplayClockOutSec(shift, abandonedHours);
  if (typeof outSec !== 'number') {
    return null;
  }
  let co = new Date(outSec * 1000);
  if (co < ciDate) {
    co = new Date(co.getTime() + 24 * 3600000);
  }
  return co;
};

// collapse duplicate shifts that have the same effective shift details
const dedupeLaborRows = (items, abandonedHours = 18) => {
  const map = new Map();

  const minuteBucket = (sec) => {
    if (!sec) return '';
    const d = new Date(sec * 1000);
    return [
      d.getFullYear(),
      String(d.getMonth() + 1).padStart(2, '0'),
      String(d.getDate()).padStart(2, '0'),
      String(d.getHours()).padStart(2, '0'),
      String(d.getMinutes()).padStart(2, '0'),
    ].join('-');
  };

  for (const s of items || []) {
    const ciSec = s?.clockIn?.seconds || null;
    if (!ciSec) continue;

    const effectiveOutSec = getDisplayClockOutSec(s, abandonedHours) ?? Math.floor(endOfDayMsForClockIn(ciSec) / 1000);

    const key = [
      String(s.name || '').trim().toLowerCase(),
      String(s.role || '').trim().toLowerCase(),
      String(s.store || '').trim().toLowerCase(),
      minuteBucket(ciSec),
      minuteBucket(effectiveOutSec),
    ].join('|');

    const existing = map.get(key);

    if (!existing) {
      map.set(key, s);
      continue;
    }

    const existingEdited = Boolean(
      existing.oldClockIn || existing.oldClockOut || existing.reason || existing.editedAt || existing.editedBy
    );
    const currentEdited = Boolean(
      s.oldClockIn || s.oldClockOut || s.reason || s.editedAt || s.editedBy
    );

    const existingHasRealOut = Boolean(existing.clockOut?.seconds);
    const currentHasRealOut = Boolean(s.clockOut?.seconds);

    const existingPathLen = Array.isArray(existing.__path) ? existing.__path.length : 0;
    const currentPathLen = Array.isArray(s.__path) ? s.__path.length : 0;

    const existingClockInSec = existing.clockIn?.seconds || 0;
    const currentClockInSec = s.clockIn?.seconds || 0;

    if (
      (!existingEdited && currentEdited) ||
      (!existingHasRealOut && currentHasRealOut) ||
      (currentPathLen > existingPathLen) ||
      (currentClockInSec > existingClockInSec)
    ) {
      map.set(key, s);
    }
  }

  return Array.from(map.values()).sort(
    (a, b) => (b.clockIn?.seconds || 0) - (a.clockIn?.seconds || 0)
  );
};


const localDateKeyFromSec = (sec) => {
  if (typeof sec !== 'number') return '';
  const d = new Date(sec * 1000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const scheduleBoundarySeconds = (dateKey, shiftText) => {
  const match = String(shiftText || '').match(/^(\d{2}):(\d{2})-(\d{2}):(\d{2})$/);
  if (!match || !dateKey) return { start: null, end: null };

  const [year, month, day] = dateKey.split('-').map(Number);
  const start = new Date(year, month - 1, day, Number(match[1]), Number(match[2]), 0, 0);
  const end = new Date(year, month - 1, day, Number(match[3]), Number(match[4]), 0, 0);
  if (end <= start) end.setDate(end.getDate() + 1);

  return {
    start: Math.floor(start.getTime() / 1000),
    end: Math.floor(end.getTime() / 1000),
  };
};

export default function LaborTab({ siteScope = { allSites: true, siteIds: [] } }) {
  const orgId = useOrgId();
  const { orgName } = useOrgMeta();
  const actor = useActor();

  // — Public holidays (org settings) —
  const [publicPayEnabled, setPublicPayEnabled] = useState(true);
  const [publicHolidays, setPublicHolidays] = useState(() => new Set());
  const [abandonedShiftHours, setAbandonedShiftHours] = useState(18);
  const laborSettingsLoadedRef = useRef(false);

  useEffect(() => {
    if (!orgId) return;
    laborSettingsLoadedRef.current = false;
    const ref = doc(db, 'orgs', orgId, 'settings', 'labor');
    // C8.5: Labor settings are configuration, not operational realtime state.
    // Read them once when Labor opens.
    getDoc(ref).then((snap) => {
      const d = snap.data() || {};
      setPublicPayEnabled(d.publicPayEnabled !== false);
      const arr = Array.isArray(d.publicHolidays) ? d.publicHolidays : [];
      setPublicHolidays(new Set(arr));
      setAbandonedShiftHours(
        Math.max(
          18,
          Math.min(
            24,
            Number.isFinite(Number(d.forceAutoClockOutHours ?? d.abandonedShiftHours))
              ? Math.round(Number(d.forceAutoClockOutHours ?? d.abandonedShiftHours))
              : 18
          )
        )
      );

      const rawSavedLunch =
        d.lunchBreakMinutes ?? d.lunchMin ?? d.lunchMinutes;
      if (rawSavedLunch !== undefined && rawSavedLunch !== null && rawSavedLunch !== '') {
        const savedLunchMin = Number(rawSavedLunch);
        if (Number.isFinite(savedLunchMin) && savedLunchMin >= 0) {
          setLunchMin(Math.round(savedLunchMin));
          localStorage.setItem('labor_lunchMin', String(Math.round(savedLunchMin)));
        }
      } else {
        // One-time migration from Labor's existing local source into the shared
        // org setting so Schedule Intelligence can consume the same value.
        const existingLocal = Number(localStorage.getItem('labor_lunchMin'));
        const migrationLunch =
          Number.isFinite(existingLocal) && existingLocal >= 0
            ? Math.round(existingLocal)
            : Math.max(0, Math.round(Number(lunchMin) || 60));

        setLunchMin(migrationLunch);
        localStorage.setItem('labor_lunchMin', String(migrationLunch));
        setDoc(
          doc(db, 'orgs', orgId, 'settings', 'labor'),
          { lunchBreakMinutes: migrationLunch, updatedAt: serverTimestamp() },
          { merge: true }
        ).catch(() => {});
      }
      laborSettingsLoadedRef.current = true;
    }).catch((error) => {
      console.error('[Labor] Failed to load labor settings', error);
      laborSettingsLoadedRef.current = true;
    });
  }, [orgId]);

  // — Filters & thresholds —
  const [stores, setStores] = useState([]);
  const [store, setStore] = useState(
    JSON.parse(localStorage.getItem('labor_store')) || { value: 'ALL', label: 'All stores' }
  );
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [names, setNames] = useState([]);
  const [staffName, setStaffName] = useState(null);
  const [lunchMin, setLunchMin] = useState(() => +localStorage.getItem('labor_lunchMin') || 60);

  // — Data & UI state —
  const [rows, setRows] = useState([]);
  const [publishedScheduleMap, setPublishedScheduleMap] = useState(() => new Map());
  const [publishedAttendanceRows, setPublishedAttendanceRows] = useState([]);
  const [didSearch, setDidSearch] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [exportConfirm, setExportConfirm] = useState(null);
  const [editShift, setEditShift] = useState(null);
  const [editConfirm, setEditConfirm] = useState(null);
  const [addShiftOpen, setAddShiftOpen] = useState(false);
  const [staffOptions, setStaffOptions] = useState([]);
  const [addShift, setAddShift] = useState({ storeId: '', staffName: '', staffId: '', role: '', date: null, inHour: 9, inMinute: 0, outHour: 17, outMinute: 0, lunchMin: 60, reason: 'Added manually' });
  const csvRef = useRef();
  const editDragRef = useRef(null);

// Tooltip originals: if shift doc doesn't carry oldClockIn/oldClockOut, fetch last EDIT_SHIFT audit entry
  const [origMap, setOrigMap] = useState({});
  const origFetchRef = useRef({});


  // maps to handle legacy data where shift.store might be the name instead of id
  const storeIdToNameRef = useRef({});
  const storeNameToIdRef = useRef({});
  const staffIdToNameRef = useRef({});


  useEffect(() => {
    if (!orgId) return;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'orgs', orgId, 'cashup_users'));
        const seen = new Set();
      const opts = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() || {}) }))
          .filter((u) => !(u.isArchived === true || u.archived === true))
          .map((u) => {
            const currentName = String(u.name || '').trim();
            const aliasSource = [
              u.previousName,
              u.oldName,
              u.legacyName,
              ...(Array.isArray(u.previousNames) ? u.previousNames : []),
              ...(Array.isArray(u.oldNames) ? u.oldNames : []),
              ...(Array.isArray(u.legacyNames) ? u.legacyNames : []),
              ...(Array.isArray(u.aliases) ? u.aliases : []),
              ...(Array.isArray(u.nameHistory) ? u.nameHistory : []),
            ];
            return {
              value: currentName,
              label: currentName,
              staffId: u.id || u.uid || u.userId || '',
              currentName,
              aliases: aliasSource.map((x) => String(x || '').trim()).filter(Boolean),
              role: String(u.roleName || u.role || '').trim(),
              storeId: u.storeId || '',
            };
          })
          .filter((u) => u.value)
          .filter((u) => {
            const key = `${String(u.value).toLowerCase()}|${String(u.storeId || '')}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .sort((a, b) => a.label.localeCompare(b.label));
        staffIdToNameRef.current = opts.reduce((acc, u) => {
          if (u.staffId) acc[String(u.staffId)] = u.currentName || u.label;
          return acc;
        }, {});
        setStaffOptions(opts);
      } catch {
        setStaffOptions([]);
      }
    })();
  }, [orgId]);

  // Published Schedule Intelligence roster is Labor's overtime source of truth.
  // Key: storeId|staffId|YYYY-MM-DD -> published shift window.
  useEffect(() => {
    if (!orgId) {
      setPublishedScheduleMap(new Map());
      return undefined;
    }

    return onSnapshot(
      collection(db, 'orgs', orgId, 'neo_schedule_entries'),
      (snap) => {
        const next = new Map();
        const attendanceRows = [];

        snap.docs.forEach((scheduleDoc) => {
          const data = scheduleDoc.data() || {};
          if (!data.storeId || !data.staffId || !data.date) return;

          const publishedShift = String(
            (data.varianceApproved === true && data.originalPublishedShift
              ? data.originalPublishedShift
              : data.publishedShift) || ''
          );

          const storeKey = String(data.storeId);
          const dateKey = String(data.date);
          const staffIdKey = String(data.staffId || '');
          const staffName = String(data.staffName || data.name || '').trim();
          const staffNameKey = staffName.toLowerCase();

          if (/^\d{2}:\d{2}-\d{2}:\d{2}$/.test(publishedShift)) {
            if (staffIdKey) {
              next.set(`${storeKey}|id:${staffIdKey}|${dateKey}`, publishedShift);
            }
            if (staffNameKey) {
              next.set(`${storeKey}|name:${staffNameKey}|${dateKey}`, publishedShift);
            }
            return;
          }

          // Paid leave / attendance with allocated hours is already a Labor event.
          // It must exist even in the future because no clock-in/out will ever be created.
          const isAttendance = data.isAttendance === true || publishedShift.startsWith('ATTENDANCE::');
          const allocateHours = data.attendanceAllocateHours === true;
          const attendanceHours = Math.max(0, Number(data.attendanceHours || data.scheduledNetHours || 0));

          if (data.published === true && isAttendance && allocateHours && attendanceHours > 0) {
            const [year, month, day] = dateKey.split('-').map(Number);
            const dateAtNoon = new Date(year, (month || 1) - 1, day || 1, 12, 0, 0, 0);
            const dateSec = Math.floor(dateAtNoon.getTime() / 1000);

            attendanceRows.push({
              id: `attendance:${scheduleDoc.id}`,
              __attendanceScheduleId: scheduleDoc.id,
              __syntheticAttendance: true,
              name: staffName,
              staffNameSnapshot: staffName,
              staffId: staffIdKey,
              role: String(data.roleName || data.role || ''),
              store: storeKey,
              storeId: storeKey,
              clockIn: { seconds: dateSec },
              clockOut: { seconds: dateSec },
              attendanceDate: dateKey,
              attendanceLabel: String(data.attendanceLabel || 'Paid leave'),
              attendanceTypeId: String(data.attendanceTypeId || ''),
              attendanceHours,
              attendancePaid: data.attendancePaid !== false,
              attendanceAllocateHours: true,
              publishedShift,
              reason: String(data.attendanceLabel || 'Paid leave'),
              source: 'schedule-attendance',
            });
          }
        });

        setPublishedScheduleMap(next);
        setPublishedAttendanceRows(attendanceRows);
      },
      (error) => {
        console.warn('Could not load published schedule for Labor overtime', error);
        setPublishedScheduleMap(new Map());
        setPublishedAttendanceRows([]);
      }
    );
  }, [orgId]);

  // — Load stores + names once (org-scoped; explicit subcollection paths) —
  useEffect(() => {
    if (!orgId) return;
    (async () => {
      // STORES — orgs/{orgId}/cashup_stores (dedupe by name)
      const storesCol = collection(db, 'orgs', orgId, 'cashup_stores');
      const ss = await getDocs(storesCol);
      const rawStores = ss.docs.map((d) => ({
        id: d.id,
        name: String(d.data()?.name || d.id).trim(),
        isArchived: !!d.data()?.isArchived,
      }));

      const seenStoreNames = new Set();
      const uniqStores = [];
      for (const s of rawStores.sort((a, b) => a.name.localeCompare(b.name))) {
        if (s.isArchived) continue;
        const key = s.name.toLowerCase();
        if (seenStoreNames.has(key)) continue;
        seenStoreNames.add(key);
        uniqStores.push(s);
      }

      const idToName = {};
      const nameToId = {};
      uniqStores.forEach((s) => {
        idToName[s.id] = s.name;
        nameToId[s.name] = s.id;
      });
      storeIdToNameRef.current = idToName;
      storeNameToIdRef.current = nameToId;

      const allowedSiteIds = new Set((siteScope.siteIds || []).map(String));
      const scopedStores = siteScope.allSites ? uniqStores : uniqStores.filter((s) => allowedSiteIds.has(String(s.id)));
      const selectOptions = [
        ...(scopedStores.length > 1 ? [{ value: 'ALL', label: 'All stores' }] : []),
        ...scopedStores.map((s) => ({ value: s.id, label: s.name })),
      ];
      setStores(selectOptions);
      if (!siteScope.allSites && scopedStores.length === 1) setStore({ value: scopedStores[0].id, label: scopedStores[0].name });

      // If a previously saved store id no longer exists, fall back to ALL
      const current = JSON.parse(localStorage.getItem('labor_store'));
      if (current?.value && current.value !== 'ALL' && !uniqStores.find((x) => x.id === current.value)) {
        setStore({ value: 'ALL', label: 'All stores' });
      }

      // STAFF — orgs/{orgId}/cashup_users (dedupe by name)
      const usersCol = collection(db, 'orgs', orgId, 'cashup_users');
      const us = await getDocs(usersCol);
      const seenNames = new Set();
      const uniqNames = [];
      for (const d of us.docs) {
        const data = d.data() || {};
        if (data.isArchived === true || data.archived === true) continue;

        const n = String(data.name || '').trim();
        if (!n) continue;

        const key = n.toLowerCase();
        if (seenNames.has(key)) continue;
        seenNames.add(key);
        const aliasSource = [
          data.previousName,
          data.oldName,
          data.legacyName,
          ...(Array.isArray(data.previousNames) ? data.previousNames : []),
          ...(Array.isArray(data.oldNames) ? data.oldNames : []),
          ...(Array.isArray(data.legacyNames) ? data.legacyNames : []),
          ...(Array.isArray(data.aliases) ? data.aliases : []),
          ...(Array.isArray(data.nameHistory) ? data.nameHistory : []),
        ];
        if (!siteScope.allSites && !allowedSiteIds.has(String(data.storeId || ''))) continue;
        uniqNames.push({
          value: n,
          label: n,
          staffId: d.id || data.uid || data.userId || '',
          currentName: n,
          aliases: aliasSource.map((x) => String(x || '').trim()).filter(Boolean),
          storeId: data.storeId || '',
          role: String(data.roleName || data.role || '').trim(),
        });
      }
            uniqNames.sort((a, b) => a.label.localeCompare(b.label));
      staffIdToNameRef.current = uniqNames.reduce((acc, u) => {
        if (u.staffId) acc[String(u.staffId)] = u.currentName || u.label;
        return acc;
      }, staffIdToNameRef.current || {});
      setNames(uniqNames);
    })();
  }, [orgId, siteScope.allSites, siteScope.siteIds]);

  // — Persist settings locally —
  useEffect(() => {
    localStorage.setItem('labor_store', JSON.stringify(store));
  }, [store]);
  useEffect(() => {
    localStorage.setItem('labor_lunchMin', String(lunchMin));
    if (!orgId || !laborSettingsLoadedRef.current) return;

    const value = Math.max(0, Math.round(Number(lunchMin) || 0));
    const t = setTimeout(() => {
      setDoc(
        doc(db, 'orgs', orgId, 'settings', 'labor'),
        { lunchBreakMinutes: value, updatedAt: serverTimestamp() },
        { merge: true }
      ).catch(() => {});
    }, 450);
    return () => clearTimeout(t);
  }, [lunchMin, orgId]);

  const resolveStoreLabel = (val) => {
    const raw = String(val ?? '').trim();
    if (!raw) return '';
    if (raw === 'ALL') return 'All Stores';

    // Prefer the loaded store map, then the active Select options/label.
    // This prevents Firestore document IDs from leaking into Labor exports
    // while the store-name map is still settling after initial load.
    const mapped = storeIdToNameRef.current[raw];
    if (mapped) return mapped;

    const option = stores.find((item) => String(item?.value ?? '') === raw);
    if (option?.label) return option.label;

    if (String(store?.value ?? '') === raw && store?.label) return store.label;
    return raw;
  };

  const normalizeStaffLookupKey = useCallback((value) => (
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '')
  ), []);

  const looksLikeSameRenamedStaff = useCallback((legacyName, currentName, aliases = []) => {
    const legacyKey = normalizeStaffLookupKey(legacyName);
    const currentKey = normalizeStaffLookupKey(currentName);
    if (!legacyKey || !currentKey) return false;
    if (legacyKey === currentKey) return true;

    const aliasKeys = (aliases || [])
      .map((item) => normalizeStaffLookupKey(item))
      .filter(Boolean);
    if (aliasKeys.includes(legacyKey)) return true;

    // Safe fallback for simple real-world renames like Mbali -> Mbalenhle.
    // Do not attach every missing legacy name in the same store/role, because that
    // leaks renamed staff rows into other staff searches with the same role.
    const minLen = Math.min(legacyKey.length, currentKey.length);
    let prefix = 0;
    while (prefix < minLen && legacyKey[prefix] === currentKey[prefix]) prefix += 1;
    if (prefix >= 3) return true;

    const legacyTokens = new Set(String(legacyName || '').toLowerCase().split(/\s+/).filter(Boolean));
    const currentTokens = new Set(String(currentName || '').toLowerCase().split(/\s+/).filter(Boolean));
    return [...legacyTokens].some((token) => token.length >= 3 && currentTokens.has(token));
  }, [normalizeStaffLookupKey]);

const ensureOriginal = useCallback(
    async (shift) => {
      if (!orgId || !shift) return;
      const key = (shift.__path || []).join('/') || shift.id;
      if (!key) return;

      // Already have originals on the shift doc itself
      if (shift.oldClockIn?.seconds || shift.oldClockOut?.seconds) return;

      // Already fetched
      if (origMap[key]) return;

      // Avoid duplicate in-flight
      if (origFetchRef.current[key]) return;
      origFetchRef.current[key] = true;

      try {
        const logsCol = collection(db, 'orgs', orgId, 'auditLogs');
        const qs = q(
          logsCol,
          where('action', '==', 'EDIT_SHIFT'),
          where('entityId', '==', shift.id),
          orderBy('createdAt', 'desc'),
          limit(1)
        );
        const snap = await getDocs(qs);
        if (snap.empty) return;

        const d = snap.docs[0].data() || {};
        const before = d.before || d.meta?.before || {};
        const clockIn = typeof before.clockIn === 'number' ? before.clockIn : null;
        const clockOut = typeof before.clockOut === 'number' ? before.clockOut : null;
        const reason = (before.reason || d.meta?.reason || d.reason || '').toString();

        setOrigMap((m) => ({
          ...m,
          [key]: {
            clockIn,
            clockOut,
            reason,
            editedBy: d.actorName || d.actorEmail || d.userEmail || d.updatedBy || '',
            editedAt: d.createdAt || d.timestamp || null,
          },
        }));
      } catch (e) {
        // ignore (tooltip is best-effort)
      } finally {
        origFetchRef.current[key] = false;
      }
    },
    [orgId, origMap]
  );


  // Default period = TODAY (start + end)
  useEffect(() => {
    if (!dateFrom) {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      setDateFrom(d);
    }
    if (!dateTo) {
      const d = new Date();
      d.setHours(23, 59, 59, 999);
      setDateTo(d);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportStoreLabel = useMemo(() => {
    if (store?.value && store.value !== 'ALL') {
      const selectedOption = stores.find(
        (item) => String(item?.value ?? '') === String(store.value)
      );
      return selectedOption?.label || store?.label || resolveStoreLabel(store.value);
    }
    return 'All Stores';
  }, [store, stores]);

  const selectedStaffLabel = useMemo(() => (staffName?.label || staffName?.value || '').trim(), [staffName]);

  const selectedStaffStoreLabel = useMemo(() => {
    if (!selectedStaffLabel) return exportStoreLabel;
    const rowStores = Array.from(
      new Set((rows || []).map((r) => resolveStoreLabel(r.store)).filter(Boolean))
    );
    if (rowStores.length === 1) return rowStores[0];
    if (store?.value && store.value !== 'ALL') return resolveStoreLabel(store.value);
    return staffName?.storeId ? resolveStoreLabel(staffName.storeId) : 'All Stores';
  }, [selectedStaffLabel, rows, store, staffName, exportStoreLabel]);

  const exportTitle = useMemo(() => {
    if (selectedStaffLabel) {
      return `${selectedStaffStoreLabel} — ${selectedStaffLabel}`.trim();
    }
    const t = `${orgName || ''} — ${exportStoreLabel}`.trim();
    return t || 'Labor Report';
  }, [orgName, exportStoreLabel, selectedStaffLabel, selectedStaffStoreLabel]);

  const exportPeriod = useMemo(() => {
    const fromStr = dateFrom ? dateFrom.toLocaleDateString() : '—';
    const toStr = dateTo ? dateTo.toLocaleDateString() : '—';
    return `Period: ${fromStr} — ${toStr}`;
  }, [dateFrom, dateTo]);

  // ---------- readers: nested first, legacy fallback ----------
  const readNestedForStore = useCallback(
    async (storeId) => {
      try {
        const col = collection(db, 'orgs', orgId, 'stores', storeId, 'shifts');
        const snap = await getDocs(col);
        return snap.docs.map((d) =>
          normalizeShiftDoc(d, ['orgs', orgId, 'stores', storeId, 'shifts', d.id], storeId)
        );
      } catch {
        return [];
      }
    },
    [orgId]
  );

  const readLegacyAllForOrg = useCallback(async () => {
    try {
      const snap = await getDocs(q(collection(db, 'cashup_shifts'), where('orgId', '==', orgId)));
      return snap.docs.map((d) => normalizeShiftDoc(d, ['cashup_shifts', d.id], d.data()?.storeId || ''));
    } catch {
      return [];
    }
  }, [orgId]);

  // — Fetch & filter —
  const handleSearch = useCallback(async () => {
    if (!orgId) return;
    setDidSearch(true);

    // inclusive end-of-day for range filter
    let end = dateTo ? new Date(dateTo) : null;
    if (end) end.setHours(23, 59, 59, 999);

    // Gather candidates:
    let candidates = [];
    const selectedId = store.value; // store dropdown holds id
    const selectedName = storeIdToNameRef.current[selectedId];

    if (selectedId !== 'ALL') {
      const [nested, legacy] = await Promise.all([
        readNestedForStore(selectedId),
        readLegacyAllForOrg(),
      ]);
      // keep legacy only for this store (id or name)
      const legacyForStore = legacy.filter(
        (s) => s.store === selectedId || s.store === selectedName
      );
      const seen = new Set();
      [...nested, ...legacyForStore].forEach((r) => {
        const k = (r.__path || []).join('/') || r.id;
        if (!seen.has(k)) {
          seen.add(k);
          candidates.push(r);
        }
      });
    } else {
      // all stores → nested for each store + org legacy, then merge
      const storeIds = stores.filter((o) => o.value !== 'ALL').map((o) => o.value);
      const nestedLists = await Promise.all(storeIds.map((sid) => readNestedForStore(sid)));
      const legacy = await readLegacyAllForOrg();

      const seen = new Set();
      [...nestedLists.flat(), ...legacy].forEach((r) => {
        const k = (r.__path || []).join('/') || r.id;
        if (!seen.has(k)) {
          seen.add(k);
          candidates.push(r);
        }
      });
    }

    if (!siteScope.allSites) {
      const allowedSiteIds = new Set((siteScope.siteIds || []).map(String));
      candidates = candidates.filter((row) => {
        const rawStore = String(row.storeId || row.store || '');
        const resolvedId = storeNameToIdRef.current[rawStore] || rawStore;
        return allowedSiteIds.has(String(resolvedId));
      });
    }

    const currentStaffNameKeys = new Set(
      (names || []).map((n) => String(n.value || n.label || '').trim().toLowerCase()).filter(Boolean)
    );

    candidates = candidates.map((s) => {
      const currentName = s.staffId ? staffIdToNameRef.current[String(s.staffId)] : '';
      return currentName ? { ...s, name: currentName } : s;
    });

    const selectedStaffNameKey = String(staffName?.value || staffName?.label || '').trim().toLowerCase();
    const selectedStaffIdKey = String(staffName?.staffId || '').trim();
    const selectedStaffStoreKey = String(staffName?.storeId || '').trim();
    const selectedStaffStoreName = selectedStaffStoreKey ? storeIdToNameRef.current[selectedStaffStoreKey] : '';
    const selectedStaffRoleKey = String(staffName?.role || '').trim().toLowerCase();

    const matchesSelectedStaff = (s) => {
      if (!staffName) return true;

      const rowStaffId = String(s.staffId || '').trim();
      const rowNameKey = String(s.name || s.staffNameSnapshot || '').trim().toLowerCase();
      const rowStoreKey = String(s.store || s.storeId || '').trim();
      const rowStoreName = storeIdToNameRef.current[rowStoreKey] || rowStoreKey;
      const rowRoleKey = String(s.role || '').trim().toLowerCase();

      // Best match: immutable staff id. This is what keeps renamed staff linked.
      if (selectedStaffIdKey && rowStaffId && selectedStaffIdKey === rowStaffId) return true;

      // Current-name match for new rows after the rename.
      if (selectedStaffNameKey && rowNameKey === selectedStaffNameKey) return true;

      // Legacy rows sometimes only saved the old name and no staff id. When that old name
      // no longer exists in the current staff list, keep it attached to the renamed staff
      // only when the row belongs to the same store and role.
      const oldNameNoLongerCurrent = rowNameKey && !currentStaffNameKeys.has(rowNameKey);
      const sameStore =
        selectedStaffStoreKey &&
        (rowStoreKey === selectedStaffStoreKey || String(rowStoreName).trim() === String(selectedStaffStoreName).trim());
      const sameRole = selectedStaffRoleKey && rowRoleKey && selectedStaffRoleKey === rowRoleKey;
      const selectedAliases = Array.isArray(staffName?.aliases) ? staffName.aliases : [];
      if (
        !rowStaffId &&
        oldNameNoLongerCurrent &&
        sameStore &&
        sameRole &&
        looksLikeSameRenamedStaff(rowNameKey, selectedStaffNameKey, selectedAliases)
      ) return true;

      return false;
    };

    const now = Date.now();

    // Filter by finished shifts (clocked out) OR implied auto-out after day end
    const filtered = candidates
      .filter((s) => {
        if (!s.clockIn?.seconds) return false;

        if (!matchesSelectedStaff(s)) return false;

        // range by clock-in date
        const ci = new Date(s.clockIn.seconds * 1000);
        if (dateFrom && ci < dateFrom) return false;
        if (end && ci > end) return false;

        // Labor is an operational view as well as a historical view. Keep currently
        // open shifts visible so managers can see staff who forgot to clock out.
        return true;
      })
      .sort((a, b) => (b.clockIn?.seconds || 0) - (a.clockIn?.seconds || 0));

    const selectedStoreId = store?.value || 'ALL';
    const attendanceForView = publishedAttendanceRows.filter((row) => {
      if (selectedStoreId !== 'ALL' && String(row.store || '') !== String(selectedStoreId)) return false;
      if (!matchesSelectedStaff(row)) return false;

      const rowDate = row.clockIn?.seconds ? new Date(row.clockIn.seconds * 1000) : null;
      if (!rowDate) return false;
      if (dateFrom && rowDate < dateFrom) return false;
      if (end && rowDate > end) return false;
      return true;
    });

    const combined = [
      ...filtered.filter((s) => !s.archived),
      ...attendanceForView,
    ].sort((a, b) => (b.clockIn?.seconds || 0) - (a.clockIn?.seconds || 0));

    setRows(dedupeLaborRows(combined, abandonedShiftHours));
  }, [orgId, store, stores, staffName, names, dateFrom, dateTo, readNestedForStore, readLegacyAllForOrg, abandonedShiftHours, looksLikeSameRenamedStaff, publishedAttendanceRows, siteScope.allSites, siteScope.siteIds]);

  // — Row calculations —
  function calcRow(s) {
    const rawCiSec = toSec(s.clockIn);
    const rawCoSec = getDisplayClockOutSec(s, abandonedShiftHours);
    const ci = new Date(rawCiSec * 1000);

    if (s.__syntheticAttendance === true) {
      const totalH = Math.max(0, Number(s.attendanceHours || 0));
      const publicKey = s.attendanceDate || `${ci.getFullYear()}-${String(ci.getMonth() + 1).padStart(2, '0')}-${String(ci.getDate()).padStart(2, '0')}`;
      const isPublic = publicPayEnabled && publicHolidays.has(publicKey) && ci.getDay() !== 0;
      const sunPay = ci.getDay() === 0 ? +(totalH * 0.5).toFixed(2) : 0;
      const publicPay = isPublic ? +totalH.toFixed(2) : 0;
      return {
        ci,
        co: ci,
        scheduledShift: s.attendanceLabel || 'Paid leave',
        totalH: +totalH.toFixed(2),
        scheduledH: +totalH.toFixed(2),
        ot: 0,
        pendingOt: 0,
        lunchH: 0,
        sunPay,
        publicPay,
      };
    }

    if (s.scheduleVarianceRejected === true && ['swap_rejected', 'unscheduled_rejected'].includes(s.scheduleVarianceType)) {
      const co = typeof rawCoSec === 'number' ? new Date(rawCoSec * 1000) : new Date();
      return { ci, co, scheduledShift: '', totalH: 0, ot: 0, pendingOt: 0, scheduledH: 0, lunchH: 0, sunPay: 0, publicPay: 0 };
    }

    const coSec = typeof rawCoSec === 'number' ? rawCoSec : Math.floor(Date.now() / 1000);
    const co = new Date(coSec * 1000);
    const dateKey = localDateKeyFromSec(rawCiSec);

    const rawStore = String(s.store || '').trim();
    const storeId = storeNameToIdRef.current[rawStore.toLowerCase()] || rawStore;

    const scheduledStaffId =
      s.scheduleVarianceType === 'swap' && s.swapExpectedStaffId
        ? String(s.swapExpectedStaffId)
        : String(s.staffId || '');

    const scheduledStaffName =
      s.scheduleVarianceType === 'swap' && s.swapExpectedStaffName
        ? String(s.swapExpectedStaffName)
        : String(s.staffNameSnapshot || s.name || '');

    const publishedShift =
      (scheduledStaffId
        ? publishedScheduleMap.get(`${storeId}|id:${scheduledStaffId}|${dateKey}`)
        : '') ||
      (scheduledStaffName
        ? publishedScheduleMap.get(`${storeId}|name:${scheduledStaffName.trim().toLowerCase()}|${dateKey}`)
        : '') ||
      // Rejected overtime must never erase the scheduled payable window.
      // Keep the schedule snapshot saved on the shift as a durable fallback.
      String(s.originalScheduledShift || s.approvedScheduleShift || '');

    const boundaries = scheduleBoundarySeconds(dateKey, publishedShift);

    let acceptedSeconds = 0;
    let pendingOutsideSeconds = 0;
    let approvedOutsideSeconds = 0;

    if (typeof boundaries.start === 'number' && typeof boundaries.end === 'number') {
      const overlapStart = Math.max(rawCiSec, boundaries.start);
      const overlapEnd = Math.min(coSec, boundaries.end);
      acceptedSeconds = Math.max(0, overlapEnd - overlapStart);

      const earlyOutside = Math.max(0, Math.min(coSec, boundaries.start) - rawCiSec);
      const lateOutside = Math.max(0, coSec - Math.max(rawCiSec, boundaries.end));
      const outsideSeconds = earlyOutside + lateOutside;

      if (s.scheduleVarianceApproved === true) {
        // Only the time outside the published schedule becomes approved overtime.
        // The scheduled overlap remains regular Labor time.
        approvedOutsideSeconds = outsideSeconds;
      } else if (s.scheduleVarianceRejected === true && s.scheduleVarianceType === 'overtime_rejected') {
        // Reject ONLY the outside-schedule portion. The published shift remains
        // payable Labor time even when Neo auto-clocked the person out later.
        acceptedSeconds = Math.max(0, boundaries.end - boundaries.start);
        pendingOutsideSeconds = 0;
        approvedOutsideSeconds = 0;
      } else {
        pendingOutsideSeconds = outsideSeconds;
      }
    } else {
      const actualSeconds = Math.max(0, coSec - rawCiSec);
      if (s.scheduleVarianceApproved === true) approvedOutsideSeconds = actualSeconds;
      else if (s.scheduleVarianceRejected === true && s.scheduleVarianceType === 'overtime_rejected') pendingOutsideSeconds = 0;
      else pendingOutsideSeconds = actualSeconds;
    }

    const totalH = +((acceptedSeconds + approvedOutsideSeconds) / 3600).toFixed(2);
    const scheduledH = +(acceptedSeconds / 3600).toFixed(2);
    const pendingOt = +(pendingOutsideSeconds / 3600).toFixed(2);
    const ot = +(approvedOutsideSeconds / 3600).toFixed(2);

    const lunchMinForRow = typeof s.lunchMin === 'number' ? s.lunchMin : lunchMin;
    const lunchH = +(lunchMinForRow / 60).toFixed(2);
    const sunPay = ci.getDay() === 0 ? +(totalH * 0.5).toFixed(2) : 0;
    const publicKey = `${ci.getFullYear()}-${String(ci.getMonth() + 1).padStart(2, '0')}-${String(ci.getDate()).padStart(2, '0')}`;
    const isPublic = publicPayEnabled && publicHolidays.has(publicKey) && ci.getDay() !== 0;
    const publicPay = isPublic ? +totalH.toFixed(2) : 0;

    return { ci, co, scheduledShift: publishedShift, totalH, scheduledH, ot, pendingOt, lunchH, sunPay, publicPay };
  }

  const laborStatusLabel = (shift) => {
    if (shift?.__syntheticAttendance === true) {
      return shift.attendanceLabel || 'Paid leave';
    }
    if (shift?.scheduleVarianceRejected === true && shift?.scheduleVarianceType === 'swap_rejected') {
      return 'Swap rejected';
    }
    if (shift?.scheduleVarianceRejected === true && shift?.scheduleVarianceType === 'overtime_rejected') {
      return 'Overtime rejected';
    }
    if (shift?.scheduleVarianceRejected === true && shift?.scheduleVarianceType === 'unscheduled_rejected') {
      return 'Unscheduled rejected';
    }

    // A forced/automatic close is an operational fact and should be visible
    // before any overtime state. Schedule Intelligence can still calculate
    // outside-schedule time separately, but the Labor status must explain why
    // the shift ended.
    if (
      shift?.autoClockedOutAtThreshold === true ||
      toSec(shift?.thresholdClockOut) != null ||
      shift?.autoClockOutMode === 'force-close'
    ) {
      return 'Auto clocked out';
    }

    if (shift?.scheduleVarianceApproved === true) {
      if (shift?.scheduleVarianceType === 'swap') return 'Approved swap';
      return shift?.scheduleVarianceType === 'overtime'
        ? 'Approved overtime'
        : 'Approved actual shift';
    }

    const result = calcRow(shift);
    if (result.pendingOt > 0) return 'Overtime pending';
    return '';
  };

  const laborStatusClass = (label) => {
    switch (label) {
      case 'Approved overtime':
        return 'border-emerald-400/40 bg-emerald-400/10 text-emerald-200';
      case 'Approved swap':
        return 'border-violet-400/40 bg-violet-400/10 text-violet-200';
      case 'Approved actual shift':
        return 'border-cyan-400/40 bg-cyan-400/10 text-cyan-200';
      case 'Auto clocked out':
        return 'border-sky-400/40 bg-sky-400/10 text-sky-200';
      case 'Overtime pending':
        return 'border-amber-400/40 bg-amber-400/10 text-amber-200';
      case 'Swap rejected':
      case 'Overtime rejected':
      case 'Unscheduled rejected':
        return 'border-rose-400/40 bg-rose-400/10 text-rose-200';
      default:
        return 'border-violet-400/40 bg-violet-400/10 text-violet-200';
    }
  };

  const totalsRow = useMemo(() => {
    return (rows || []).reduce(
      (acc, s) => {
        const { totalH, ot, pendingOt, lunchH, sunPay, publicPay } = calcRow(s);
        acc.totalH += Number(totalH || 0);
        acc.ot += Number(ot || 0);
        acc.pendingOt += Number(pendingOt || 0);
        acc.lunchH += Number(lunchH || 0);
        acc.sunPay += Number(sunPay || 0);
        acc.publicPay += Number(publicPay || 0);
        return acc;
      },
      { totalH: 0, ot: 0, pendingOt: 0, lunchH: 0, sunPay: 0, publicPay: 0 }
    );
  }, [rows, publishedScheduleMap, lunchMin, publicPayEnabled, publicHolidays, abandonedShiftHours]);

  // — PDF export —
  const doPdfExport = () => {
    try {
      const docPdf = new jsPDF('l', 'pt', 'letter');
      docPdf.setFontSize(14);
      docPdf.text(exportTitle, 40, 30);
      docPdf.setFontSize(11);
      docPdf.setTextColor('#6b7280');
      docPdf.text(exportPeriod, 40, 46);
      docPdf.setTextColor('#000');

      const head = [['Name', 'Status', 'Date', 'Scheduled', 'In', 'Out', 'Store', 'Role', 'Total', 'OT', 'Pending OT', 'Lunch', 'Sunday', 'Public']];
      const body = rows.map((s) => {
        const { ci, co, scheduledShift, totalH, ot, pendingOt, lunchH, sunPay, publicPay } = calcRow(s);
        return [
          s.name,
          laborStatusLabel(s),
          ci.toLocaleDateString(),
          scheduledShift || '—',
          s.__syntheticAttendance === true ? '—' : ci.toLocaleTimeString('en-GB', { hour12: false, hour: '2-digit', minute: '2-digit' }),
          s.__syntheticAttendance === true ? '—' : (typeof getDisplayClockOutSec(s, abandonedShiftHours) === 'number'
            ? formatTimeSec(getDisplayClockOutSec(s, abandonedShiftHours))
            : 'On shift'),
          resolveStoreLabel(s.store),
          s.role || '',
          totalH.toFixed(2),
          ot.toFixed(2),
          pendingOt.toFixed(2),
          lunchH.toFixed(2),
          sunPay.toFixed(2),
          publicPay.toFixed(2),
        ];
      });
      autoTable(docPdf, {
        head,
        body,
        theme: 'striped',
        headStyles: { fillColor: '#334155', textColor: '#ffffff' },
        startY: 70,
      });
      const safe = (s) => String(s || "").replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_+|_+$/g, "");
      docPdf.save(`labor_${safe(exportTitle) || "report"}.pdf`);
    } catch (err) {
      setExportError({ title: 'PDF Export Error', message: err.message });
    }
  };

  // — Excel export —
  const doExcelExport = () => {
    try {
      const wb = XLSX.utils.book_new();
      const wsData = [
        [exportTitle],
        [exportPeriod],
        [],
        ['Name', 'Status', 'Date', 'Scheduled', 'In', 'Out', 'Store', 'Role', 'Total', 'OT', 'Pending OT', 'Lunch', 'Sunday', 'Public'],
        ...rows.map((s) => {
          const { ci, co, scheduledShift, totalH, ot, pendingOt, lunchH, sunPay, publicPay } = calcRow(s);
          return [
            s.name,
            laborStatusLabel(s),
            ci.toLocaleDateString(),
            scheduledShift || '—',
            s.__syntheticAttendance === true ? '—' : ci.toLocaleTimeString('en-GB', { hour12: false, hour: '2-digit', minute: '2-digit' }),
            s.__syntheticAttendance === true ? '—' : (typeof getDisplayClockOutSec(s, abandonedShiftHours) === 'number' ? formatTimeSec(getDisplayClockOutSec(s, abandonedShiftHours)) : 'On shift'),
            resolveStoreLabel(s.store),
            s.role || '',
            totalH.toFixed(2),
            ot.toFixed(2),
            pendingOt.toFixed(2),
            lunchH.toFixed(2),
            sunPay.toFixed(2),
            publicPay.toFixed(2),
          ];
        }),
      ];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      XLSX.utils.book_append_sheet(wb, ws, 'Labor');
      const safe = (s) => String(s || '').replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');
      XLSX.writeFile(wb, `labor_${safe(exportTitle) || 'report'}.xlsx`);
    } catch (err) {
      setExportError({ title: 'Excel Export Error', message: err.message });
    }
  };

const doSave = async () => {
  const original = rows.find((r) => r.id === editShift.id);
  if (!original) return;

  const sameMinute = (a, b) => {
    const aa = toSec(a);
    const bb = toSec(b);
    if (typeof aa !== 'number' || typeof bb !== 'number') return false;
    return Math.floor(aa / 60) === Math.floor(bb / 60);
  };

  const sameDay = (a, b) => {
    const aa = toSec(a);
    const bb = toSec(b);
    if (typeof aa !== 'number' || typeof bb !== 'number') return false;
    const da = new Date(aa * 1000);
    const db = new Date(bb * 1000);
    return (
      da.getFullYear() === db.getFullYear() &&
      da.getMonth() === db.getMonth() &&
      da.getDate() === db.getDate()
    );
  };

  const newRow = { ...editShift };
  const legacyAutoDerived = isLegacyEndOfDayAutoOut(original, abandonedShiftHours);

  if (!newRow.oldClockIn) {
    if (toSec(original.oldClockIn) != null) {
      newRow.oldClockIn = original.oldClockIn;
    } else {
      newRow.oldClockIn = original.clockIn;
    }
  }

  if (!newRow.oldClockOut) {
    if (toSec(original.oldClockOut) != null) {
      newRow.oldClockOut = original.oldClockOut;
    } else if (toSec(original.clockOut) != null) {
      newRow.oldClockOut = original.clockOut;
    } else if (toSec(original.clockIn) != null) {
      newRow.oldClockOut = { seconds: Math.floor(endOfDayMsForClockIn(toSec(original.clockIn)) / 1000) };
    }
  }

  const nextClockOutSec = toSec(newRow.clockOut);
  const thresholdClockOutToSave = legacyAutoDerived
    ? asTS(nextClockOutSec)
    : (newRow.thresholdClockOut ? asTS(toSec(newRow.thresholdClockOut)) : null);

  const ref = doc(db, ...(newRow.__path || ['cashup_shifts', newRow.id]));
  await updateDoc(ref, {
    clockIn: asTS(toSec(newRow.clockIn)),
    clockOut: asTS(nextClockOutSec),
    ...(typeof newRow.lunchMin === 'number' ? { lunchMin: newRow.lunchMin } : {}),
    reason: newRow.reason || '',
    ...(newRow.oldClockIn ? { oldClockIn: asTS(toSec(newRow.oldClockIn)) } : {}),
    ...(newRow.oldClockOut ? { oldClockOut: asTS(toSec(newRow.oldClockOut)) } : {}),
    ...(newRow.actualClockOut ? { actualClockOut: asTS(toSec(newRow.actualClockOut)) } : { actualClockOut: null }),
    thresholdClockOut: thresholdClockOutToSave || null,
    autoClockedOutAtThreshold: legacyAutoDerived ? true : !!newRow.autoClockedOutAtThreshold,
    editedAt: serverTimestamp(),
    editedBy: actor?.name || auth?.currentUser?.email || auth?.currentUser?.uid || 'unknown',
    editedByUid: actor?.uid || auth?.currentUser?.uid || '',
    editedByEmail: actor?.email || auth?.currentUser?.email || '',
    updatedAt: serverTimestamp(),
  });

  const allCandidates = [];

  try {
    const nestedCol = collection(db, 'orgs', orgId, 'stores', newRow.store, 'shifts');
    const nestedSnap = await getDocs(nestedCol);
    nestedSnap.docs.forEach((d) => {
      allCandidates.push(
        normalizeShiftDoc(
          d,
          ['orgs', orgId, 'stores', newRow.store, 'shifts', d.id],
          newRow.store
        )
      );
    });
  } catch {}

  try {
    const legacySnap = await getDocs(q(collection(db, 'cashup_shifts'), where('orgId', '==', orgId)));
    legacySnap.docs.forEach((d) => {
      allCandidates.push(
        normalizeShiftDoc(d, ['cashup_shifts', d.id], d.data()?.storeId || '')
      );
    });
  } catch {}

  const duplicateOriginals = allCandidates.filter((r) => {
    if (!r || r.id === newRow.id) return false;
    if (r.archived) return false;

    const samePerson =
      String(r.name || '').trim().toLowerCase() === String(newRow.name || '').trim().toLowerCase();

    const sameRole =
      String(r.role || '').trim().toLowerCase() === String(newRow.role || '').trim().toLowerCase();

    const sameStore =
      String(r.store || '').trim().toLowerCase() === String(newRow.store || '').trim().toLowerCase();

    const sameShiftDay = sameDay(r.clockIn, newRow.clockIn);

    if (!samePerson || !sameRole || !sameStore || !sameShiftDay) return false;

    const rWasEdited = Boolean(r.oldClockIn || r.oldClockOut || r.reason || r.editedAt || r.editedBy);
    if (rWasEdited) return false;

    const matchesOriginalStart =
      sameMinute(r.clockIn, original.clockIn) ||
      sameMinute(r.clockIn, newRow.oldClockIn);

    const matchesOriginalEnd =
      sameMinute(r.clockOut, original.clockOut) ||
      sameMinute(r.clockOut, newRow.oldClockOut);

    return matchesOriginalStart && matchesOriginalEnd;
  });

  for (const dup of duplicateOriginals) {
    try {
      const dupRef = doc(db, ...(dup.__path || ['cashup_shifts', dup.id]));
      await updateDoc(dupRef, {
        archived: true,
        archivedAt: serverTimestamp(),
        archivedBy: actor?.name || actor?.email || null,
        reason: dup.reason || newRow.reason || 'Superseded by edited shift',
        supersededByShiftId: newRow.id,
        updatedAt: serverTimestamp(),
      });
    } catch {}
  }

  setRows((rs) =>
    rs
      .map((r) =>
        r.id === newRow.id
          ? {
              ...newRow,
              ...(legacyAutoDerived && nextClockOutSec
                ? {
                    thresholdClockOut: { seconds: nextClockOutSec },
                    autoClockedOutAtThreshold: true,
                  }
                : {}),
            }
          : r
      )
      .filter((r) => !duplicateOriginals.some((d) => d.id === r.id))
  );

  toast.success('Shift updated');

  logAudit(db, actor, {
    action: 'EDIT_SHIFT',
    page: 'Labor',
    entityType: 'shift',
    entityId: newRow.id,
    entityName: `${newRow.staffName || 'Shift'} (${newRow.dayLabel || ''})`.trim(),
    storeId: store?.value && store.value !== 'ALL' ? store.value : '',
    before: {
      clockIn: toSec(original.clockIn),
      clockOut: toSec(original.clockOut),
      lunchMin: original.lunchMin ?? null,
      reason: original.reason || '',
    },
    after: {
      clockIn: toSec(newRow.clockIn),
      clockOut: toSec(newRow.clockOut),
      lunchMin: typeof newRow.lunchMin === 'number' ? newRow.lunchMin : null,
      reason: newRow.reason || '',
    },
  });

  setEditShift(null);
  setEditConfirm(null);
};


  const submitAddShift = async () => {
    if (!orgId) return;
    if (!addShift.storeId || !addShift.staffName || !addShift.role || !addShift.date) {
      toast.error('Please complete store, staff, role, date and hours.');
      return;
    }

    const base = new Date(addShift.date);
    base.setHours(0, 0, 0, 0);
    const clockIn = new Date(base);
    clockIn.setHours(Number(addShift.inHour || 0), Number(addShift.inMinute || 0), 0, 0);
    const clockOut = new Date(base);
    clockOut.setHours(Number(addShift.outHour || 0), Number(addShift.outMinute || 0), 0, 0);
    if (clockOut <= clockIn) {
      clockOut.setDate(clockOut.getDate() + 1);
    }

    const selectedStore = stores.find((s) => s.value === addShift.storeId);
    await addDoc(collection(db, 'orgs', orgId, 'stores', addShift.storeId, 'shifts'), {
      orgId,
      storeId: addShift.storeId,
      store: addShift.storeId,
      storeName: selectedStore?.label || addShift.storeId,
      name: addShift.staffName,
      staffNameSnapshot: addShift.staffName,
      staffId: addShift.staffId || '',
      role: addShift.role,
      clockIn: Timestamp.fromDate(clockIn),
      clockOut: Timestamp.fromDate(clockOut),
      lunchMin: Number(addShift.lunchMin || 0),
      reason: String(addShift.reason || 'Added manually').trim(),
      active: false,
      addedManually: true,
      createdAt: serverTimestamp(),
      createdBy: actor?.name || actor?.email || '',
      createdByUid: actor?.uid || '',
      createdByEmail: actor?.email || '',
      editedAt: serverTimestamp(),
      editedBy: actor?.name || actor?.email || '',
      editedByUid: actor?.uid || '',
      editedByEmail: actor?.email || '',
    });

    toast.success('Shift added');
    setAddShiftOpen(false);
    await handleSearch();
  };

  // — Archive shift —
  const doArchive = async () => {
    // Optimistic update
    setRows((rs) => rs.filter((r) => r.id !== editShift.id));
    const p = editShift.__path || ['cashup_shifts', editShift.id];
    await updateDoc(doc(db, ...p), {
      archived: true,
      archivedAt: serverTimestamp(),
      archivedBy: actor?.name || actor?.email || null,
      // keep the same "reason" field used for edits so archives have a visible rationale
      reason: (editShift?.reason || '').trim(),
      // snapshot for audit/review in Settings → Archived Shifts
      archivedSnapshot: {
        clockIn: editShift?.clockIn?.seconds ?? null,
        clockOut: editShift?.clockOut?.seconds ?? null,
        lunchMin: typeof editShift?.lunchMin === 'number' ? editShift.lunchMin : null,
        storeId: editShift?.storeId || null,
        staffId: editShift?.staffId || null,
      },
    });

    // Audit (best-effort)
    try {
      await logAudit(db, actor, {
        action: 'ARCHIVE_SHIFT',
        page: 'Labor',
        entityType: 'shift',
        entityId: editShift.id,
        entityName: editShift.staffName || editShift.staffId || 'shift',
        storeId: editShift.storeId || null,
      });
    } catch (e) {}

    setEditShift(null);
    setEditConfirm(null);
  };

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-2xl font-semibold">Labor Management</h1>

      {!orgId && (
        <div className="rounded bg-rose-900/20 border border-rose-700/50 px-3 py-2 text-rose-200">
          No <code>orgId</code> found on your token; contact the admin.
        </div>
      )}

      {/* — Filters & Search — */}
      <div className="neo-labor-filter-grid grid grid-cols-12 gap-3 items-end">
        <div className="col-span-3">
          <Select options={stores} value={store} onChange={setStore} styles={selectStyles} theme={selectTheme} />
        </div>
        <div className="col-span-2">
          <DatePicker
            selected={dateFrom}
            onChange={setDateFrom}
            placeholderText="From date"
            className="w-full px-2 py-1 bg-[#1e293b] text-white rounded"
          />
        </div>
        <div className="col-span-2">
          <DatePicker
            selected={dateTo}
            onChange={setDateTo}
            placeholderText="To date"
            className="w-full px-2 py-1 bg-[#1e293b] text-white rounded"
          />
        </div>
        <div className="col-span-3">
          <Select
            options={names}
            value={staffName}
            onChange={setStaffName}
            isClearable
            placeholder="Staff name…"
            styles={selectStyles}
            theme={selectTheme}
          />
        </div>
        <div className="col-span-2 text-right">
          <button
            onClick={handleSearch}
            disabled={!orgId}
            className="px-4 py-2 bg-teal-500 text-white rounded disabled:opacity-60"
          >
            Search
          </button>
        </div>

        <div className="col-span-6 flex flex-wrap items-center gap-2">
          <span className="whitespace-nowrap text-sm text-slate-300">Overtime:</span>
          <span className="whitespace-nowrap rounded-full border border-cyan-400/30 bg-cyan-400/10 px-3 py-1 text-xs font-semibold text-cyan-200">
            Published Schedule
          </span>
          <label className="ml-2 whitespace-nowrap">Lunch (min):</label>
          <input
            type="number"
            value={lunchMin}
            onChange={(e) => setLunchMin(+e.target.value)}
            className="w-20 px-2 py-1 bg-[#1e293b] text-white rounded"
          />
        </div>

        {didSearch && (
          <div className="col-span-6 flex flex-nowrap items-center justify-end gap-2 overflow-x-auto">
            <button
              onClick={() => setAddShiftOpen(true)}
              className="shrink-0 whitespace-nowrap px-3 py-2 text-sm bg-emerald-600 text-white rounded"
            >
              Add Shift
            </button>
            <button
              onClick={() => setExportConfirm('pdf')}
              className="shrink-0 whitespace-nowrap px-3 py-2 text-sm bg-gray-600 text-white rounded"
            >
              Export PDF
            </button>
            <button
              onClick={() => setExportConfirm('excel')}
              className="shrink-0 whitespace-nowrap px-3 py-2 text-sm bg-gray-600 text-white rounded"
            >
              Export EXCEL
            </button>
            <button
              onClick={() => setExportConfirm('csv')}
              className="shrink-0 whitespace-nowrap px-3 py-2 text-sm bg-gray-600 text-white rounded"
            >
              Export CSV
            </button>
          </div>
        )}
      </div>

      {/* — Data Table — */}
      <div className="neo-mobile-table-scroll rounded border border-white/10 overflow-x-auto">
        <table className="min-w-[1380px] w-full text-sm whitespace-nowrap">
          <thead className="bg-gray-700">
            <tr>
              <th className="p-2 text-left">Name</th>
              <th className="p-2 text-center">Status</th>
              <th className="p-2 text-center">Date</th>
              <th className="p-2 text-center">Scheduled</th>
              <th className="p-2 text-center">In</th>
              <th className="p-2 text-center">Out</th>
              <th className="p-2 text-center">Store</th>
              <th className="p-2 text-center">Role</th>
              <th className="p-2 text-center">Total hrs</th>
              <th className="p-2 text-center">Overtime</th>
              <th className="p-2 text-center">Pending OT</th>
              <th className="p-2 text-center">Lunch</th>
              <th className="p-2 text-right">Sunday pay</th>
              <th className="p-2 text-right">Public pay</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan="14" className="p-4 text-center">
                  No results yet.
                </td>
              </tr>
            ) : (
              rows.map((s) => {
                const { ci, co, scheduledShift, totalH, ot, pendingOt, lunchH, sunPay, publicPay } = calcRow(s);
                const baseInStr = ci.toLocaleTimeString('en-GB', { hour12: false, hour: '2-digit', minute: '2-digit' });

                // Edited marker: show a star when a shift has been edited (old clock in/out or reason)
                const wasEdited = Boolean(s.oldClockIn || s.oldClockOut || s.reason || s.editedAt || s.editedBy);
                const inStr = wasEdited ? `★${baseInStr}` : baseInStr;

                // Out display: show *23:59 when auto-out (no clockOut stored)
                const baseOutStr = !s.clockOut?.seconds && !s.approvedActualClockOutSec
                  ? 'On shift'
                  : typeof getDisplayClockOutSec(s, abandonedShiftHours) === 'number'
                    ? formatTimeSec(getDisplayClockOutSec(s, abandonedShiftHours))
                    : 'On shift';
                const outStr = wasEdited && baseOutStr !== '*23:59' ? `★${baseOutStr}` : baseOutStr;

                const key = (s.__path || []).join('/') || s.id;
                const orig = origMap[key];
                const hasInfo = Boolean(s.oldClockIn || s.oldClockOut || s.reason || s.editedAt || s.editedBy || s.scheduleVarianceApproved || orig);
                const isAttendanceRow = s.__syntheticAttendance === true;
                const openEdit = () => {
                  if (!isAttendanceRow) setEditShift(s);
                };

                return (
                  <tr key={(s.__path || []).join('/') || s.id} className="hover:bg-[#1e293b]">
                    <td className="p-2 flex items-center gap-2">
                      {hasInfo && (
                        <Tooltip
                          placement="top"
                          disablePortal
                          content={
                            <div className="bg-[#1e293b] text-white p-2 rounded max-w-xs">
                              <p>
                                <b>Original:</b>&nbsp;
                                {(() => {
                                  const oci =
                                    toSec(s.oldClockIn) ??
                                    orig?.clockIn ??
                                    toSec(s.clockIn) ??
                                    null;

                                  const oco =
                                    toSec(s.oldClockOut) ??
                                    orig?.clockOut ??
                                    getOriginalClockOutSec(s, abandonedShiftHours) ??
                                    toSec(s.clockOut) ??
                                    null;

                                  const fmt = (sec) =>
                                    typeof sec === 'number'
                                      ? new Date(sec * 1000).toLocaleTimeString('en-GB', {
                                          hour12: false,
                                          hour: '2-digit',
                                          minute: '2-digit',
                                        })
                                      : '--:--';                                  
                                    return (
                                    <>
                                      {fmt(oci)}&nbsp;–&nbsp;{fmt(oco)}
                                    </>
                                  );
                                 })()}
                              </p>
                                                            <p>
                                <b>Latest:</b>&nbsp;{inStr}&nbsp;–&nbsp;{outStr}
                              </p>
                              {(s.reason || orig?.reason) && (
                                <p>
                                  <b>Reason:</b> {s.reason || orig?.reason}
                                </p>
                              )}
                              {(s.editedBy || orig?.editedBy) && (
                                <p>
                                  <b>Edited by:</b> {s.editedBy || orig?.editedBy}
                                </p>
                              )}
                              {s.scheduleVarianceApproved && (
                                <>
                                  <p>
                                    <b>Schedule approval:</b> Actual shift approved
                                  </p>
                                  {s.originalScheduledShift && (
                                    <p>
                                      <b>Scheduled before:</b> {s.originalScheduledShift}
                                    </p>
                                  )}
                                  {s.approvedScheduleShift && (
                                    <p>
                                      <b>Approved shift:</b> {s.approvedScheduleShift}
                                    </p>
                                  )}
                                  {s.scheduleVarianceApprovedByName && (
                                    <p>
                                      <b>Approved by:</b> {s.scheduleVarianceApprovedByName}
                                    </p>
                                  )}
                                </>
                              )}
                              {(s.editedAt || orig?.editedAt) && (
                                <p>
                                  <b>Edited at:</b>{' '}
                                  {(() => {
                                    const v = s.editedAt || orig?.editedAt;
                                    const dt =
                                      v?.toDate ? v.toDate()
                                      : v?.seconds ? new Date(v.seconds * 1000)
                                      : v ? new Date(v)
                                      : null;
                                    return dt ? dt.toLocaleString('en-GB') : '--';
                                  })()}
                                </p>
                              )}
                            </div>
                          }
                          offset={[0, 8]}
                        >
                          <span onMouseEnter={() => ensureOriginal(s)} className="text-gray-400 hover:text-white cursor-pointer">ℹ︎</span>
                        </Tooltip>
                      )}
                      <span onClick={openEdit} className="cursor-pointer">
                        {s.name}
                      </span>
                    </td>
                    <td className="p-2 text-center whitespace-nowrap">
                      {laborStatusLabel(s) ? (
                        <span
                          className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-semibold ${laborStatusClass(laborStatusLabel(s))}`}
                        >
                          {laborStatusLabel(s)}
                        </span>
                      ) : (
                        <span className="text-xs text-white/25">—</span>
                      )}
                    </td>
                    <td className="p-2 text-center cursor-pointer whitespace-nowrap" onClick={openEdit}>
                      {ci.toLocaleDateString()}
                    </td>
                    <td className="p-2 text-center cursor-pointer whitespace-nowrap text-cyan-200" onClick={openEdit}>
                      {scheduledShift || '—'}
                    </td>
                    <td className="p-2 text-center cursor-pointer whitespace-nowrap" onClick={openEdit}>
                      {isAttendanceRow ? '—' : inStr}
                    </td>
                    <td className="p-2 text-center cursor-pointer whitespace-nowrap" onClick={openEdit}>
                      {isAttendanceRow ? (
                        <span className="text-white/35">—</span>
                      ) : !s.clockOut?.seconds && !s.approvedActualClockOutSec ? (
                        <span className="inline-flex rounded-full border border-cyan-400/30 bg-cyan-400/10 px-2.5 py-1 text-xs font-semibold text-cyan-200">
                          On shift
                        </span>
                      ) : (
                        outStr
                      )}
                    </td>
                    <td className="p-2 text-center cursor-pointer whitespace-nowrap" onClick={openEdit}>
                      {resolveStoreLabel(s.store)}
                    </td>
                    <td className="p-2 text-center cursor-pointer whitespace-nowrap" onClick={openEdit}>
                      {s.role || ''}
                    </td>
                    <td className="p-2 text-center cursor-pointer whitespace-nowrap" onClick={openEdit}>
                      {totalH.toFixed(2)}
                    </td>
                    <td className="p-2 text-center cursor-pointer whitespace-nowrap" onClick={openEdit}>
                      {ot.toFixed(2)}
                    </td>
                    <td className="p-2 text-center cursor-pointer whitespace-nowrap" onClick={openEdit}>
                      {pendingOt > 0 ? (
                        <span className="inline-flex rounded-full border border-amber-400/30 bg-amber-400/10 px-2 py-1 text-[10px] font-semibold text-amber-200">
                          {pendingOt.toFixed(2)}
                        </span>
                      ) : (
                        '0.00'
                      )}
                    </td>
                    <td className="p-2 text-center cursor-pointer whitespace-nowrap" onClick={openEdit}>
                      {lunchH.toFixed(2)}
                    </td>
                    <td className="p-2 text-right cursor-pointer whitespace-nowrap" onClick={openEdit}>
                      {sunPay.toFixed(2)}
                    </td>
                    <td className="p-2 text-right cursor-pointer whitespace-nowrap" onClick={openEdit}>
                      {publicPay.toFixed(2)}
                    </td>
                  </tr>
                );
              })
            )}
            {rows.length > 0 && (
              <tr className="bg-white/5 font-semibold">
                <td className="p-2">Totals</td>
                <td className="p-2 text-center"></td>
                <td className="p-2 text-center"></td>
                <td className="p-2 text-center"></td>
                <td className="p-2 text-center"></td>
                <td className="p-2 text-center"></td>
                <td className="p-2 text-center"></td>
                <td className="p-2 text-center"></td>
                <td className="p-2 text-center">{totalsRow.totalH.toFixed(2)}</td>
                <td className="p-2 text-center">{totalsRow.ot.toFixed(2)}</td>
                <td className="p-2 text-center">{totalsRow.pendingOt.toFixed(2)}</td>
                <td className="p-2 text-center">{totalsRow.lunchH.toFixed(2)}</td>
                <td className="p-2 text-right">{totalsRow.sunPay.toFixed(2)}</td>
                <td className="p-2 text-right">{totalsRow.publicPay.toFixed(2)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* — Hidden CSVLink — */}
      <CSVLink
        ref={csvRef}
        data={[
          [exportTitle],
          [exportPeriod],
          [],
          ['Name', 'Status', 'Date', 'Scheduled', 'In', 'Out', 'Store', 'Role', 'Total', 'OT', 'Pending OT', 'Lunch', 'Sunday', 'Public'],
          ...rows.map((s) => {
            const { ci, co, scheduledShift, totalH, ot, pendingOt, lunchH, sunPay, publicPay } = calcRow(s);
            return [
              s.name,
              laborStatusLabel(s),
              ci.toLocaleDateString(),
              scheduledShift || '—',
              s.__syntheticAttendance === true ? '—' : ci.toLocaleTimeString('en-GB', { hour12: false }),
              s.__syntheticAttendance === true ? '—' : (typeof getDisplayClockOutSec(s, abandonedShiftHours) === 'number' ? formatTimeSec(getDisplayClockOutSec(s, abandonedShiftHours)) : 'On shift'),
              resolveStoreLabel(s.store),
              s.role || '',
              totalH.toFixed(2),
              ot.toFixed(2),
              pendingOt.toFixed(2),
              lunchH.toFixed(2),
              sunPay.toFixed(2),
              publicPay.toFixed(2),
            ];
          }),
        ]}
        filename="labor_report.csv"
        className="hidden"
      />

      {/* — Export Confirmation — */}
      {exportConfirm && (
        <Modal title={`Confirm ${exportConfirm.toUpperCase()}`} onClose={() => setExportConfirm(null)} className="max-w-sm">
          <p>
            Ready to export <b>{exportConfirm.toUpperCase()}</b>?
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <button className="px-4 py-2 bg-gray-600 text-white rounded" onClick={() => setExportConfirm(null)}>
              Cancel
            </button>
            {exportConfirm === 'pdf' && (
              <button
                className="px-4 py-2 bg-green-600 text-white rounded"
                onClick={() => {
                  doPdfExport();
                  setExportConfirm(null);
                }}
              >
                Yes, export PDF
              </button>
            )}
            {exportConfirm === 'excel' && (
              <button
                className="px-4 py-2 bg-green-600 text-white rounded"
                onClick={() => {
                  doExcelExport();
                  setExportConfirm(null);
                }}
              >
                Yes, export EXCEL
              </button>
            )}
            {exportConfirm === 'csv' && (
              <button
                className="px-4 py-2 bg-green-600 text-white rounded"
                onClick={() => {
                  csvRef.current.link.click();
                  setExportConfirm(null);
                }}
              >
                Yes, export CSV
              </button>
            )}
          </div>
        </Modal>
      )}

      {/* — Export Error — */}
      {exportError && (
        <Modal title={exportError.title} onClose={() => setExportError(null)} className="max-w-md">
          <p className="text-red-400">{exportError.message}</p>
        </Modal>
      )}

      {/* — Edit Shift Modal — */}

      {addShiftOpen && (
        <Modal title="Add new shift" onClose={() => setAddShiftOpen(false)}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block mb-1 font-medium">Store</label>
              <Select
                options={stores.filter((s) => s.value !== 'ALL')}
                value={stores.find((s) => s.value === addShift.storeId) || null}
                onChange={(opt) => setAddShift((s) => ({ ...s, storeId: opt?.value || '' }))}
                styles={selectStyles}
                theme={selectTheme}
              />
            </div>
            <div>
              <label className="block mb-1 font-medium">Staff</label>
              <Select
                options={staffOptions.filter((u) => !addShift.storeId ? true : u.storeId === addShift.storeId)}
                value={staffOptions.find((u) => u.value === addShift.staffName && (!addShift.storeId || u.storeId === addShift.storeId)) || null}
                onChange={(opt) => setAddShift((s) => ({ ...s, staffName: opt?.value || '', staffId: opt?.staffId || '', role: opt?.role || s.role, storeId: s.storeId || opt?.storeId || '' }))}
                styles={selectStyles}
                theme={selectTheme}
              />
            </div>
            <div>
              <label className="block mb-1 font-medium">Date</label>
              <DatePicker
                selected={addShift.date}
                onChange={(d) => setAddShift((s) => ({ ...s, date: d }))}
                dateFormat="dd/MM/yyyy"
                className="w-full px-2 py-1 bg-[#334155] rounded"
              />
            </div>
            <div>
              <label className="block mb-1 font-medium">Role</label>
              <input value={addShift.role} onChange={(e)=>setAddShift((s)=>({ ...s, role: e.target.value }))} className="w-full px-2 py-1 bg-[#334155] rounded" />
            </div>
            <div>
              <label className="block mb-1 font-medium">Clock in</label>
              <div className="flex gap-2">
                <input type="number" min={0} max={23} value={addShift.inHour} onChange={(e)=>setAddShift((s)=>({ ...s, inHour: Number(e.target.value||0) }))} className="w-24 px-2 py-1 bg-[#334155] rounded" />
                <input type="number" min={0} max={59} value={addShift.inMinute} onChange={(e)=>setAddShift((s)=>({ ...s, inMinute: Number(e.target.value||0) }))} className="w-24 px-2 py-1 bg-[#334155] rounded" />
              </div>
            </div>
            <div>
              <label className="block mb-1 font-medium">Clock out</label>
              <div className="flex gap-2">
                <input type="number" min={0} max={23} value={addShift.outHour} onChange={(e)=>setAddShift((s)=>({ ...s, outHour: Number(e.target.value||0) }))} className="w-24 px-2 py-1 bg-[#334155] rounded" />
                <input type="number" min={0} max={59} value={addShift.outMinute} onChange={(e)=>setAddShift((s)=>({ ...s, outMinute: Number(e.target.value||0) }))} className="w-24 px-2 py-1 bg-[#334155] rounded" />
              </div>
              <div className="text-xs text-white/60 mt-1">If clock out is earlier than clock in, it will continue into the next day.</div>
            </div>
            <div>
              <label className="block mb-1 font-medium">Lunch break (minutes)</label>
              <input type="number" min={0} step={5} value={addShift.lunchMin} onChange={(e)=>setAddShift((s)=>({ ...s, lunchMin: Number(e.target.value||0) }))} className="w-full px-2 py-1 bg-[#334155] rounded" />
            </div>
            <div className="md:col-span-2">
              <label className="block mb-1 font-medium">Reason</label>
              <textarea value={addShift.reason} onChange={(e)=>setAddShift((s)=>({ ...s, reason: e.target.value }))} className="w-full h-24 px-2 py-1 bg-[#334155] rounded" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button className="px-4 py-2 bg-gray-600 text-white rounded" onClick={() => setAddShiftOpen(false)}>Cancel</button>
            <button className="px-4 py-2 bg-emerald-600 text-white rounded" onClick={submitAddShift}>Add shift</button>
          </div>
        </Modal>
      )}

      {editShift && (
        <Draggable nodeRef={editDragRef} handle=".modal-header">
          <div ref={editDragRef}>
            <Modal
              title={`Edit Shift — ${editShift.name} on ${new Date(
                editShift.clockIn.seconds * 1000
              ).toLocaleDateString()}`}
              onClose={() => {
                setEditShift(null);
                setEditConfirm(null);
              }}
              className="max-w-lg max-h-[90vh] overflow-y-auto"
            >
            {/** Require a reason before allowing Save / Archive */}
            {(() => {
              const ok = String(editShift?.reason || '').trim().length > 0;
              return (
                <>
            {/* Clock-In Date */}
            <label className="block mb-1 font-medium">Clock-In Date</label>
            <DatePicker
              selected={new Date(editShift.clockIn.seconds * 1000)}
              onChange={(d) => {
                const sec = d.getTime() / 1000;
                setEditShift({ ...editShift, clockIn: { seconds: sec } });
              }}
              dateFormat="dd/MM/yyyy"
              className="w-full px-2 py-1 bg-[#334155] rounded mb-4"
            />

            {/* Clock In */}
            <div className="mb-4">
              <label className="block mb-1 font-medium">Clock In</label>
              <div className="flex gap-2">
                <Select
                  options={Array.from({ length: 24 }, (_, i) => ({ value: i, label: String(i).padStart(2, '0') }))}
                  value={{
                    value: new Date(editShift.clockIn.seconds * 1000).getHours(),
                    label: String(new Date(editShift.clockIn.seconds * 1000).getHours()).padStart(2, '0'),
                  }}
                  onChange={(o) => {
                    const d = new Date(editShift.clockIn.seconds * 1000);
                    d.setHours(o.value);
                    setEditShift({ ...editShift, clockIn: { seconds: d.getTime() / 1000 } });
                  }}
                  styles={selectStyles}
                  theme={selectTheme}
                />
                <Select
                  options={Array.from({ length: 60 }, (_, i) => ({ value: i, label: String(i).padStart(2, '0') }))}
                  value={{
                    value: new Date(editShift.clockIn.seconds * 1000).getMinutes(),
                    label: String(new Date(editShift.clockIn.seconds * 1000).getMinutes()).padStart(2, '0'),
                  }}
                  onChange={(o) => {
                    const d = new Date(editShift.clockIn.seconds * 1000);
                    d.setMinutes(o.value);
                    setEditShift({ ...editShift, clockIn: { seconds: d.getTime() / 1000 } });
                  }}
                  styles={selectStyles}
                  theme={selectTheme}
                />
              </div>
              {editShift.oldClockIn && Math.floor((editShift.oldClockIn.seconds || 0)/60) !== Math.floor((editShift.clockIn?.seconds || 0)/60) && (
                <p className="mt-1 text-xs text-gray-400">
                  Original:{' '}
                  {new Date(editShift.oldClockIn.seconds * 1000).toLocaleTimeString('en-GB', {
                    hour12: false,
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              )}
            </div>

            {/* Clock Out */}
            <div className="mb-4">
              <label className="block mb-1 font-medium">Clock Out</label>
              {!editShift.clockOut?.seconds && !editShift.approvedActualClockOutSec ? (
                <div className="mb-2 inline-flex rounded-full border border-cyan-400/30 bg-cyan-400/10 px-2.5 py-1 text-xs font-semibold text-cyan-200">
                  On shift · no clock-out recorded yet
                </div>
              ) : null}
              <div className="flex gap-2">
                <Select
                  options={Array.from({ length: 24 }, (_, i) => ({ value: i, label: String(i).padStart(2, '0') }))}
                  value={
                    typeof getEditClockOutSec(editShift, abandonedShiftHours) === 'number'
                      ? {
                          value: new Date(getEditClockOutSec(editShift, abandonedShiftHours) * 1000).getHours(),
                          label: String(new Date(getEditClockOutSec(editShift, abandonedShiftHours) * 1000).getHours()).padStart(2, '0'),
                        }
                      : {
                          value: new Date().getHours(),
                          label: String(new Date().getHours()).padStart(2, '0'),
                        }
                  }
                  onChange={(o) => {
                    const currentMinutes = typeof getEditClockOutSec(editShift, abandonedShiftHours) === 'number'
                      ? new Date(getEditClockOutSec(editShift, abandonedShiftHours) * 1000).getMinutes()
                      : new Date().getMinutes();
                    const nextSec = buildEditedClockOutSec(editShift, o?.value, currentMinutes, abandonedShiftHours);
                    if (typeof nextSec !== 'number') return;
                    setEditShift({
                      ...editShift,
                      clockOut: { seconds: nextSec },
                      thresholdClockOut: null,
                      autoClockedOutAtThreshold: false,
                    });
                  }}
                  styles={selectStyles}
                  theme={selectTheme}
                />
                <Select
                  options={Array.from({ length: 60 }, (_, i) => ({ value: i, label: String(i).padStart(2, '0') }))}
                  value={
                    typeof getEditClockOutSec(editShift, abandonedShiftHours) === 'number'
                      ? {
                          value: new Date(getEditClockOutSec(editShift, abandonedShiftHours) * 1000).getMinutes(),
                          label: String(new Date(getEditClockOutSec(editShift, abandonedShiftHours) * 1000).getMinutes()).padStart(2, '0'),
                        }
                      : {
                          value: new Date().getMinutes(),
                          label: String(new Date().getMinutes()).padStart(2, '0'),
                        }
                  }
                  onChange={(o) => {
                    const currentHours = typeof getEditClockOutSec(editShift, abandonedShiftHours) === 'number'
                      ? new Date(getEditClockOutSec(editShift, abandonedShiftHours) * 1000).getHours()
                      : new Date().getHours();
                    const nextSec = buildEditedClockOutSec(editShift, currentHours, o?.value, abandonedShiftHours);
                    if (typeof nextSec !== 'number') return;
                    setEditShift({
                      ...editShift,
                      clockOut: { seconds: nextSec },
                      thresholdClockOut: null,
                      autoClockedOutAtThreshold: false,
                    });
                  }}
                  styles={selectStyles}
                  theme={selectTheme}
                />
              </div>
              {typeof getOriginalClockOutSec(editShift, abandonedShiftHours) === 'number' &&
                Math.floor((getOriginalClockOutSec(editShift, abandonedShiftHours) || 0) / 60) !==
                  Math.floor((getDisplayClockOutSec(editShift, abandonedShiftHours) || 0) / 60) && (
                <p className="mt-1 text-xs text-gray-400">
                  Original:{' '}
                  {formatTimeSec(getOriginalClockOutSec(editShift, abandonedShiftHours))}
                </p>
              )}
              <div className="mt-2 flex items-center gap-2">
                {(() => {
                  const originalSec = getOriginalClockOutSec(editShift, abandonedShiftHours);
                  const editSec = getEditClockOutSec(editShift, abandonedShiftHours);
                  const restorable = canRestoreShift(editShift, abandonedShiftHours);
                  const approvalLocked = editShift.scheduleVarianceApproved === true;

                  return (
                    <>
                      <button
                        type="button"
                        className={`px-3 py-1 rounded text-sm text-white ${restorable ? 'bg-sky-700 hover:bg-sky-600' : 'bg-slate-700/60 cursor-not-allowed'}`}
                        disabled={!restorable}
                        onClick={() => {
                          if (!restorable) return;
                          setEditShift({
                            ...editShift,
                            clockOut: { seconds: originalSec },
                            actualClockOut: { seconds: originalSec },
                            thresholdClockOut: null,
                            autoClockedOutAtThreshold: false,
                          });
                        }}
                      >
                        Restore
                      </button>
                      <span className={`text-xs ${approvalLocked ? 'text-emerald-300/80' : 'text-white/60'}`}>
                        {approvalLocked
                          ? 'Restore locked — this actual shift was approved in Schedule Intelligence.'
                          : 'Restores the original manual clock-out when one exists.'}
                      </span>
                    </>
                  );
                })()}
              </div>
            </div>

            {editShift.scheduleVarianceApproved && (
              <div className="mb-4 rounded border border-emerald-400/30 bg-emerald-400/10 p-3 text-sm text-emerald-100">
                <div className="font-semibold">✓ {editShift.scheduleVarianceType === 'swap' ? 'Swap approved' : editShift.scheduleVarianceType === 'overtime' ? 'Overtime approved' : 'Actual shift approved'} from Schedule Intelligence</div>
                <div className="mt-1 text-xs text-emerald-100/80">
                  {editShift.originalScheduledShift ? `Scheduled: ${editShift.originalScheduledShift}` : 'Previously unscheduled'}
                  {editShift.approvedScheduleShift ? ` · Approved: ${editShift.approvedScheduleShift}` : ''}
                  {editShift.scheduleVarianceApprovedByName ? ` · By ${editShift.scheduleVarianceApprovedByName}` : ''}
                </div>
                <div className="mt-2 text-xs font-semibold text-emerald-200">
                  Labor now treats the approved actual shift as the accepted working period. Restore is locked for this record.
                </div>
              </div>
            )}

            {/* Reason + Lunch override */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block mb-1 font-medium">Reason</label>
                <textarea
                  value={editShift.reason || ''}
                  onChange={(e) => setEditShift({ ...editShift, reason: e.target.value })}
                  className="w-full h-24 px-2 py-1 bg-[#334155] rounded"
                />
                <div className="text-xs text-white/60 mt-1">
                  Please provide a reason before saving or archiving this shift.
                </div>
              </div>
              <div>
                <label className="block mb-1 font-medium">Lunch break (minutes)</label>
                <input
                  type="number"
                  min={0}
                  step={5}
                  value={typeof editShift.lunchMin === 'number' ? editShift.lunchMin : lunchMin}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setEditShift({ ...editShift, lunchMin: Number.isFinite(v) ? v : 0 });
                  }}
                  className="w-full px-2 py-1 bg-[#334155] rounded"
                />
                <div className="text-xs text-white/60 mt-1">This overrides the default lunch setting for this shift only.</div>
              </div>
            </div>

            {/* Actions */}
            {(editShift.editedBy || editShift.editedAt) && (
              <div className="text-xs text-white/60 mb-3">
                Last edited{' '}
                {editShift.editedBy ? (
                  <>
                    by <span className="text-white/80">{editShift.editedBy}</span>
                  </>
                ) : null}
                {editShift.editedAt?.seconds ? (
                  <>
                    {' '}
                    on{' '}
                    <span className="text-white/80">
                      {new Date(editShift.editedAt.seconds * 1000).toLocaleString()}
                    </span>
                  </>
                ) : null}
              </div>
            )}
            <div className="flex justify-between">
              <button
                className={`px-4 py-2 text-white rounded ${ok ? 'bg-red-600' : 'bg-red-600/40 cursor-not-allowed'}`}
                disabled={!ok}
                onClick={() => ok && setEditConfirm('archive')}
              >
                Archive
              </button>
              <button
                className={`px-4 py-2 text-white rounded ${ok ? 'bg-green-600' : 'bg-green-600/40 cursor-not-allowed'}`}
                disabled={!ok}
                onClick={() => ok && setEditConfirm('save')}
              >
                Save
              </button>
              <button className="px-4 py-2 bg-gray-600 text-white rounded" onClick={() => setEditShift(null)}>
                Cancel
              </button>
            </div>

            {/* Confirm dialog */}
            {editConfirm && (
              <Modal
                title={editConfirm === 'save' ? 'Confirm Save' : 'Confirm Archive'}
                onClose={() => setEditConfirm(null)}
                className="max-w-sm"
              >
                <p>
                  Are you sure you want to <b>{editConfirm === 'save' ? 'save' : 'archive'}</b> this shift?
                </p>
                <div className="mt-4 flex justify-end gap-2">
                  <button className="px-4 py-2 bg-gray-600 text-white rounded" onClick={() => setEditConfirm(null)}>
                    Cancel
                  </button>
                  <button
                    className={`px-4 py-2 text-white rounded ${
                      editConfirm === 'save' ? (ok ? 'bg-green-600' : 'bg-green-600/40 cursor-not-allowed') : (ok ? 'bg-red-600' : 'bg-red-600/40 cursor-not-allowed')
                    }`}
                    disabled={!ok}
                    onClick={() => {
                      if (!ok) return;
                      editConfirm === 'save' ? doSave() : doArchive();
                    }}
                  >
                    Yes, {editConfirm === 'save' ? 'save' : 'archive'}
                  </button>
                </div>
              </Modal>
            )}
                </>
              );
            })()}
            </Modal>
          </div>
        </Draggable>
      )}
    </div>
  );
}
